import * as Yup from 'yup'
import { PrimaryButton } from '../Atoms/Buttons'
import {
  GeneralParams,
  PieceDimensionMax,
  PieceInterface,
  ServiceDTO,
} from '../../interfaces'
import { FormText } from '../FormFields/FormText'
import { useAppSelector } from '../../store/hooks'
import { usePieceDimensionMax } from '../../hooks/usePieceDimensionMax'
import { maxPieceDimensionMessage } from '../../utils/shipmentValidation'
import { FormSelect } from '../FormFields/FormSelect'
import { PlusIcon } from '@heroicons/react/24/outline'
import { handleNumberChange } from '../../utils/format'
import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'
import { PackageTypeEnum } from '../../interfaces/Shipment/PackageTypeEnum'
import { PaymentModeId } from '../../interfaces/Shipment/PaymentModeEnum'
import { getChargedWeight } from '../../services'

export interface PieceFormValues {
  category: string
  height: string
  width: string
  length: string
  weight: string
  declaredValue: string
  amount: number
  type?: string
}
interface PieceField {
  shipment: {
    pieces: PieceInterface[]
    packageType: string
    valueDeclarate: string
    serviceData?: ServiceDTO
    paymentModeID?: PaymentModeId
  }
  newPiece: PieceFormValues
}
interface PieceFormProps<T> {
  formik: FormikProps<T & PieceField>
}

export const PieceForm = <T extends any>({ formik }: PieceFormProps<T>) => {
  const errors = formik.errors.newPiece as
    | FormikErrors<PieceFormValues>
    | undefined

  const touched = formik.touched.newPiece as
    | FormikTouched<PieceFormValues>
    | undefined

  const pieceCategories = useAppSelector(
    (state) => state.inmutable.PieceCategories,
  )
  const user = useAppSelector((state) => state.user)

  const [errorBoleean, setErrorBoleean] = useState<number>(0)
  const [isSobre, setIsSobre] = useState(false)

  // [Peso a facturar] Bloqueo previo al "Agregar": el backend calcula el peso a
  // facturar (mayor entre balanza y volumétrico) de piezas actuales + la nueva;
  // si supera el tope del servicio, la pieza no se agrega y se muestra el detalle.
  const [checkingWeight, setCheckingWeight] = useState(false)
  const [overweight, setOverweight] = useState<{
    charged: number
    max: number
    physical: number
    dimensional: number
  } | null>(null)

  // La lista de piezas puede cambiar desde afuera (eliminar en PieceTable): el
  // aviso describe un intento de agregado que ya no corresponde — se limpia.
  useEffect(() => {
    setOverweight(null)
  }, [formik.values.shipment.pieces])

  // Qué campos marcar en rojo cuando el agregado quedó bloqueado por sobrepeso:
  // si lo que excede es el volumétrico → Alto/Ancho/Largo; si no → Peso. El error
  // ' ' (espacio) pinta el ring sin repetir el texto: el banner de abajo explica
  // (mismo patrón que los tests de volumen/densidad).
  const overweightByDimensions =
    !!overweight && overweight.dimensional > overweight.physical
  const overweightByWeight = !!overweight && !overweightByDimensions

  const parameterDensityMax = useAppSelector(
    (state) => state.inmutable.densityMax,
  )

  const parameterVolumeMin = useAppSelector(
    (state) => state.inmutable.volumenMin,
  )

  useEffect(() => {
    formik.setFieldValue(
      'newPiece.type',
      formik.values.shipment.packageType ?? PackageTypeEnum.BOX,
    )
  }, [formik.values.shipment.packageType])

  const dimensionMax = usePieceDimensionMax()

  /**
   * Tope por dimensión (cm) publicado por el Facturador en `dimensionMax`
   * (MAXLARGO / MAXALTO / MAXANCHO). Reemplaza el tope que estaba cableado en el
   * front: si el parámetro no está configurado para el país, esa dimensión NO
   * tiene tope.
   *
   * Va como test aparte y ÚLTIMO del encadenado a propósito: `validate()` reduce
   * `error.inner` quedándose con el último mensaje, y los tests de volumen/densidad
   * usan ' ' (avisan con el banner). Si fuera antes, su mensaje quedaría en blanco.
   */
  const maxDimensionTest =
    (dimension: keyof PieceDimensionMax) =>
    (value: string | undefined, context: Yup.TestContext) => {
      const max = dimensionMax[dimension]
      if (!value || max === null || +value <= max) return true
      return context.createError({ message: maxPieceDimensionMessage(max) })
    }

  const validationSchema = Yup.object().shape({
    category: Yup.string().required('Este campo es requerido'),
    weight: Yup.string()
      .test('min-weight', 'Debe ser mayor a 0', (value) => {
        if (!value) return false
        return +value > 0
      })
      .test('service-weight-limits', (value, context) => {
        const serviceData = context.parent.serviceData // Accede al serviceData
        if (!serviceData || !value) return true

        const weight = +value
        const min = serviceData.minWeight ?? 0
        const max = serviceData.maxWeight ?? Infinity

        if (min !== undefined && weight < min) {
          return context.createError({
            message: `El peso debe ser ≥ ${min}kg`,
          })
        }

        if (max !== undefined && weight > max) {
          return context.createError({
            message: `El peso debe ser ≤ ${max}kg`,
          })
        }
        const currentPieces = context.parent.pieces || []
        const newWeight = +value
        // La pieza entra "Cantidad" veces: el total suma peso × cantidad.
        const amount = +(context.parent.amount ?? 1) || 1
        const totalWeight =
          currentPieces.reduce(
            (sum: number, piece: PieceInterface) => sum + piece.weight,
            0,
          ) +
          newWeight * amount
        const maxWeight = serviceData.maxWeight
        if (maxWeight && totalWeight > maxWeight) {
          return context.createError({
            message: `Peso total sería ${
              Math.round(totalWeight * 100) / 100
            }kg (máximo permitido: ${maxWeight}kg)`,
          })
        }

        return true
      })
      .required('Este campo es requerido'),
    height: Yup.string()
      .test('min-height', 'Debe ser mayor a 0', (value) => {
        if (!value) return false
        return +value > 0
      })
      .test('volumenDimensity', ' ', (value, config) => {
        if (!value) return false
        var volumenTotal =
          Number(config.parent.length ?? 0) *
          Number(config.parent.height ?? 0) *
          Number(config.parent.width ?? 0)

        let volumen = parameterVolumeMin[0].valueGeneral

        if (volumenTotal < Number(volumen)) setErrorBoleean(errorBoleean + 1)

        return volumenTotal > Number(volumen)
      })
      .test('parameterDensityMax', ' ', (value, object) => {
        if (!value) return false

        if (object.parent.type === '10' || object.parent.type === undefined)
          return true

        var volumen =
          Number(object.parent.length ?? 0) *
          Number(object.parent.height ?? 0) *
          Number(object.parent.width ?? 0)
        var total = Number(object.parent.weight ?? 0) / volumen

        if (total > Number(parameterDensityMax[0].valueGeneral))
          setErrorBoleean(errorBoleean + 1)
        return total < Number(parameterDensityMax[0].valueGeneral)
      })
      .test('dimension-max', ' ', maxDimensionTest('height'))
      .required('Este campo es requerido'),
    width: Yup.string()
      .test('min-width', 'Debe ser mayor a 0', (value) => {
        if (!value) return false
        return +value > 0
      })
      .test('volumenDimensity', ' ', (value, config) => {
        if (!value) return false
        var volumenTotal =
          Number(config.parent.length ?? 0) *
          Number(config.parent.height ?? 0) *
          Number(config.parent.width ?? 0)

        let volumen = parameterVolumeMin[0].valueGeneral

        if (volumenTotal < Number(volumen)) setErrorBoleean(errorBoleean + 1)

        return volumenTotal > Number(volumen)
      })
      .test('parameterDensityMax', ' ', (value, object) => {
        if (!value) return false

        if (object.parent.type === '10' || object.parent.type === undefined)
          return true

        var volumen =
          Number(object.parent.length ?? 0) *
          Number(object.parent.height ?? 0) *
          Number(object.parent.width ?? 0)
        var total = Number(object.parent.weight ?? 0) / volumen

        if (total > Number(parameterDensityMax[0].valueGeneral))
          setErrorBoleean(errorBoleean + 1)

        return total < Number(parameterDensityMax[0].valueGeneral)
      })
      .test('dimension-max', ' ', maxDimensionTest('width'))
      .required('Este campo es requerido'),
    length: Yup.string()
      .test('min-length', 'Debe ser mayor a 0', (value) => {
        if (!value) return false
        return +value > 0
      })
      .test('volumenDimensity', ' ', (value, config) => {
        if (!value) return false
        var volumenTotal =
          Number(config.parent.length ?? 0) *
          Number(config.parent.height ?? 0) *
          Number(config.parent.width ?? 0)

        let volumen = parameterVolumeMin[0].valueGeneral

        if (volumenTotal < Number(volumen)) setErrorBoleean(errorBoleean + 1)
        return volumenTotal > Number(volumen)
      })
      .test('parameterDensityMax', ' ', (value, object) => {
        if (!value) return false

        if (object.parent.type === '10' || object.parent.type === undefined)
          return true

        var volumen =
          Number(object.parent.length ?? 0) *
          Number(object.parent.height ?? 0) *
          Number(object.parent.width ?? 0)
        var total = Number(object.parent.weight ?? 0) / volumen

        if (total > Number(parameterDensityMax[0].valueGeneral))
          setErrorBoleean(errorBoleean + 1)

        return total < Number(parameterDensityMax[0].valueGeneral)
      })
      .test('dimension-max', ' ', maxDimensionTest('length'))
      .required('Este campo es requerido'),
    amount: Yup.string()
      .test('min-amount', 'Debe ser mayor a 0', (value) => {
        if (!value) return false
        return +value > 0
      })
      .required('Este campo es requerido'),
  })

  const validate = () => {
    try {
      validationSchema.validateSync(
        {
          ...formik.values.newPiece,
          serviceData: formik.values.shipment.serviceData, // Incluye serviceData
          pieces: formik.values.shipment.pieces,
        },
        {
          abortEarly: false,
        },
      )
      return true
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors: FormikErrors<T & PieceField> = error.inner.reduce(
          (errors, err) => {
            return { ...errors, [err.path!]: err.message }
          },
          {},
        )
        console.log(
          '[PesoFacturar] validate() local bloqueó el agregado (no se llegó a GetChargedWeight)',
          {
            errores: errors,
            todosLosErrores: error.inner.map((e) => ({
              campo: e.path,
              mensaje: e.message,
            })),
          },
        )
        formik.setFormikState({
          ...formik,
          errors: { ...formik.errors, newPiece: errors },
          touched: {
            ...formik.touched,
            newPiece: {
              category: true,
              weight: true,
              declaredValue: true,
              height: true,
              width: true,
              length: true,
              amount: true,
            },
          },
        })
      }
      return false
    }
  }

  useEffect(() => {
    // Verificar si el servicio es sobre (ID=61)
    const servicioEsSobre = formik.values.shipment.serviceData?.serviceID === 61
    setIsSobre(servicioEsSobre)

    if (servicioEsSobre) {
      // Establecer categoría por defecto a 39 si es sobre
      formik.setFieldValue('newPiece.category', '39')
      // Limitar la cantidad a 1 si es sobre
      formik.setFieldValue('newPiece.amount', 1)
      formik.setFieldValue('newPiece.height', 30)
      formik.setFieldValue('newPiece.width', 23)
      formik.setFieldValue('newPiece.length', 1)
    } else {
      // Limpiar categoría si no es sobre
      if (formik.values.newPiece.category === '39') {
        formik.setFieldValue('newPiece.category', '')
      }

      formik.setFieldValue('newPiece.amount', 1)
      formik.setFieldValue('newPiece.height', '')
      formik.setFieldValue('newPiece.width', '')
      formik.setFieldValue('newPiece.length', '')
    }

    formik.setFieldValue(
      'newPiece.type',
      formik.values.shipment.packageType ?? PackageTypeEnum.BOX,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.shipment.packageType,
    formik.values.shipment.serviceData?.serviceID,
  ])

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex w-full flex-col sm:flex-row gap-4">
        <div className="flex flex-1">
          <FormSelect
            id="newPiece.category"
            name="newPiece.category"
            label="Categoría"
            selected={formik.values.newPiece.category}
            options={
              isSobre
                ? ['39'] // Solo mostrar opción 39 si es sobre
                : pieceCategories.map((category) => category.id)
            }
            error={
              touched?.category && errors?.category
                ? errors?.category
                : undefined
            }
            disabled={isSobre} // Deshabilitar si es sobre
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs mb-1"
            containerClassname="flex-1"
            optionString={(option) =>
              pieceCategories.find((c) => c.id == option)?.name ?? ''
            }
            onSelectOption={(option) =>
              formik.setFieldValue('newPiece.category', option)
            }
          />
        </div>

        <div className="w-full sm:w-24">
          <FormText
            id="newPiece.weight"
            name="newPiece.weight"
            label="Peso en kg."
            autoComplete="off"
            value={formik.values.newPiece.weight}
            error={
              touched?.weight && errors?.weight
                ? errors?.weight
                : overweightByWeight
                  ? ' '
                  : undefined
            }
            onChange={(e) => {
              setErrorBoleean(0)
              setOverweight(null)
              handleNumberChange(e, formik, true)
            }}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs mb-1"
          />
        </div>

        <div className="w-full sm:w-40 hidden">
          <FormText
            id="newPiece.declaredValue"
            name="newPiece.declaredValue"
            label="Valor declarado en dólares"
            autoComplete="off"
            value={formik.values.newPiece.declaredValue}
            error={
              touched?.declaredValue && errors?.declaredValue
                ? errors?.declaredValue
                : undefined
            }
            onChange={(e) => () => {
              setErrorBoleean(0)
              handleNumberChange(e, formik, true)
            }}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs mb-1"
          />
        </div>
      </div>

      <div className="flex w-full flex-col gap-1">
        <span className="text-xs font-bold text-gray-700">Dimensiones</span>

        <div className="flex w-full flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <FormText
              id="newPiece.height"
              name="newPiece.height"
              label="Alto (cm)"
              autoComplete="off"
              disabled={formik.values.shipment.serviceData?.serviceID === 61}
              value={formik.values.newPiece.height}
              error={
                touched?.height && errors?.height
                  ? errors?.height
                  : overweightByDimensions
                    ? ' '
                    : undefined
              }
              onChange={(e) => {
                setErrorBoleean(0)
                setOverweight(null)
                handleNumberChange(e, formik, false)
              }}
              onBlur={formik.handleBlur}
              className="!h-8"
              labelClassname="!text-xs mb-1"
            />
          </div>

          <div className="flex-1">
            <FormText
              id="newPiece.width"
              name="newPiece.width"
              label="Ancho (cm)"
              autoComplete="off"
              disabled={formik.values.shipment.serviceData?.serviceID === 61}
              value={formik.values.newPiece.width}
              error={
                touched?.width && errors?.width
                  ? errors?.width
                  : overweightByDimensions
                    ? ' '
                    : undefined
              }
              onChange={(e) => {
                setErrorBoleean(0)
                setOverweight(null)
                handleNumberChange(e, formik, false)
              }}
              onBlur={formik.handleBlur}
              className="!h-8"
              labelClassname="!text-xs mb-1"
            />
          </div>

          <div className="flex-1">
            <FormText
              id="newPiece.length"
              name="newPiece.length"
              label="Largo (cm)"
              autoComplete="off"
              disabled={formik.values.shipment.serviceData?.serviceID === 61}
              value={formik.values.newPiece.length}
              error={
                touched?.length && errors?.length
                  ? errors?.length
                  : overweightByDimensions
                    ? ' '
                    : undefined
              }
              onChange={(e) => {
                setErrorBoleean(0)
                setOverweight(null)
                handleNumberChange(e, formik, false)
              }}
              onBlur={formik.handleBlur}
              className="!h-8"
              labelClassname="!text-xs mb-1"
            />
          </div>
        </div>
      </div>
      {errorBoleean > 0 && (
        <div className="flex h-full flex-col gap-12">
          <p className=" text-sm text-red-500">
            Las medidas son inválidas, por favor revíselas y corríjalas
          </p>
        </div>
      )}

      <div className="flex w-full justify-between items-end gap-6">
        <FormText
          id="newPiece.amount"
          name="newPiece.amount"
          label="Cantidad"
          autoComplete="off"
          disabled={formik.values.shipment.serviceData?.serviceID === 61}
          value={formik.values.newPiece.amount}
          error={touched?.amount && errors?.amount ? errors?.amount : undefined}
          onChange={(e) => {
            setOverweight(null)
            handleNumberChange(e, formik, false)
          }}
          onBlur={formik.handleBlur}
          className="!h-8"
          labelClassname="!text-xs mb-1"
          style={{ maxWidth: '10rem' }}
        />

        <PrimaryButton
          type="button"
          onClick={async () => {
            if (checkingWeight) return
            if (!validate()) return

            // [Peso a facturar] Se consulta al backend ANTES de agregar: el peso
            // volumétrico depende de factores de BD (cuenta/servicio/modo de pago)
            // y no se puede calcular en el front. Si piezas actuales + nueva
            // superan el tope del servicio, se avisa aquí en vez de dejar que el
            // servidor rechace la guía al final (shipping.ShipmentHeader.35).
            // Snapshot de lo VALIDADO: el await de abajo abre una ventana en la
            // que el usuario podría editar campos; la pieza que se agrega debe
            // ser exactamente la que pasó validate() y la consulta de peso.
            const newPiece = { ...formik.values.newPiece }
            const amount = +newPiece.amount || 1

            const serviceData = formik.values.shipment.serviceData
            const maxWeight = serviceData?.maxWeight
            console.log('[PesoFacturar] intento de agregar pieza', {
              newPiece,
              amount,
              serviceID: serviceData?.serviceID,
              serviceName: serviceData?.serviceShortName,
              maxWeight,
              paymentModeID: formik.values.shipment.paymentModeID,
              piezasActuales: formik.values.shipment.pieces,
            })
            if (!maxWeight) {
              console.warn(
                '[PesoFacturar] SIN VALIDAR: el servicio no tiene maxWeight configurado — la pieza se agrega sin consultar GetChargedWeight',
                serviceData,
              )
            }
            if (maxWeight) {
              const candidatePiece = {
                weight: +newPiece.weight,
                height: +newPiece.height,
                width: +newPiece.width,
                length: +newPiece.length,
              }

              setCheckingWeight(true)
              const weights = await getChargedWeight(
                user?.client?.id,
                user?.client?.accountSiteID ?? undefined,
                serviceData?.serviceID,
                formik.values.shipment.paymentModeID,
                [
                  ...formik.values.shipment.pieces,
                  ...new Array(amount).fill(candidatePiece),
                ],
              )
              setCheckingWeight(false)

              console.log('[PesoFacturar] respuesta GetChargedWeight', weights)

              // Si el cálculo falla se deja pasar: la validación de peso físico
              // sigue aplicando y el backend rechaza igual la guía si excede.
              if (weights.didError || !weights.model) {
                console.warn(
                  '[PesoFacturar] SIN VALIDAR: GetChargedWeight falló o vino sin model — la pieza se agrega igual',
                  {
                    didError: weights.didError,
                    message: weights.message,
                    errorMessage: weights.errorMessage,
                  },
                )
              }
              if (!weights.didError && weights.model) {
                const { chargedWeight, physicalWeight, dimensionalWeight } =
                  weights.model
                console.log('[PesoFacturar] decisión', {
                  chargedWeight,
                  physicalWeight,
                  dimensionalWeight,
                  maxWeight,
                  bloquea: chargedWeight > maxWeight,
                  excedePorVolumen: dimensionalWeight > physicalWeight,
                })
                if (chargedWeight > maxWeight) {
                  setOverweight({
                    charged: chargedWeight,
                    max: maxWeight,
                    physical: physicalWeight,
                    dimensional: dimensionalWeight,
                  })
                  return
                }
              }
              setOverweight(null)
            }

            setErrorBoleean(0)
            let piecesList = formik.values.shipment.pieces
            let countPieces = piecesList.length + 1

            let piecesData = formik.values.shipment.valueDeclarate
            piecesData = piecesData === undefined ? '0' : piecesData
            let declareValue = 0.0
            let total = parseFloat(piecesData ?? '0') / Number(countPieces)

            declareValue = total ?? 0
            declareValue = Number.isNaN(declareValue) ? 0 : declareValue

            piecesList.map((pieces) => {
              pieces.declaredValue = declareValue
            })

            const piece: PieceInterface = {
              id: '',
              category: pieceCategories.find((c) => c.id == newPiece.category)!,
              weight: +newPiece.weight,
              declaredValue: +declareValue,
              height: +newPiece.height,
              width: +newPiece.width,
              length: +newPiece.length,
            }
            const newPieces = new Array(amount).fill(piece)

            // Forma funcional: tras el await, formik capturado en el click puede
            // estar desactualizado; prev garantiza no pisar otros campos.
            formik.setFormikState((prev) => ({
              ...prev,
              errors: {
                ...prev.errors,
                newPiece: {},
                preShipment: { pieces: '' },
              },
              touched: {
                ...prev.touched,
                newPiece: {
                  category: false,
                  weight: false,
                  declaredValue: false,
                  height: false,
                  width: false,
                  length: false,
                  amount: false,
                  type: false,
                },
              },
              values: {
                ...prev.values,
                newPiece: {
                  category: '',
                  height: '',
                  width: '',
                  length: '',
                  weight: '',
                  declaredValue: '',
                  amount: 1,
                  type: prev.values.shipment.packageType,
                },
                shipment: {
                  ...prev.values.shipment,
                  pieces: [...prev.values.shipment.pieces, ...newPieces],
                },
              },
            }))
          }}
          disabled={
            !formik.values.shipment.serviceData ||
            checkingWeight ||
            (isSobre && formik.values.shipment.pieces.length >= 1)
          }
          className="flex items-center gap-1"
        >
          <PlusIcon className="h-4 w-4" />
          {checkingWeight ? 'Verificando...' : 'Agregar'}
        </PrimaryButton>
      </div>

      {overweight && (
        <div className="w-full rounded-lg bg-red-100 border border-red-400 text-red-700 p-2 text-sm">
          <p className="text-xs">
            No se puede agregar la pieza: el peso a facturar (
            <span className="font-semibold">
              {overweight.charged.toFixed(2)}kg.
            </span>
            ) supera el máximo permitido (
            <span className="font-semibold">{overweight.max}kg.</span>).{' '}
            {overweight.dimensional > overweight.physical
              ? `El peso volumétrico (${overweight.dimensional.toFixed(
                  2,
                )}kg.) es el que excede: verifique las dimensiones (alto, ancho y largo) y, si son correctas, distribuya el contenido en más guías.`
              : 'Verifique el peso de las piezas y, si es correcto, distribuya el contenido en más guías.'}
          </p>
        </div>
      )}

      {!formik.values.shipment.serviceData && (
        <div className="flex w-full justify-between items-end gap-6">
          <span className="text-red-500 text-xs text-right w-full -mt-5">
            Seleccione un Servicio para agregar la pieza
          </span>
        </div>
      )}
    </div>
  )
}
